/**
 * Helper functions for Rule Engine integration with QuikFlow
 */

export interface RuleEnginePayload {
  name: string;
  description?: string;
  sourceStep: string; // Parent node UID
  targetStep: string; // Child node UID
  connectionType: 'onCompleteStep' | 'onFailedStep' | 'onParallelFlow';
  conditions: Array<{
    stepName: string;
    stepId: string;
    fieldName: string;
    operator: string;
    value: any;
  }>;
  actions: Array<{
    type: string;
    stepName: string;
    stepId: string;
    fieldName?: string;
    value?: any;
  }>;
}

export interface ConnectionInfo {
  parentNodeUid: string;
  childNodeUid: string;
  connectionType: 'onCompleteStep' | 'onFailedStep' | 'onParallelFlow';
  stepType: string;
}

/**
 * Creates a rule engine payload for a new node connection
 */
export const createRuleEnginePayload = (
  connectionInfo: ConnectionInfo,
  ruleName: string,
  conditions: Array<any> = [],
  actions: Array<any> = []
): RuleEnginePayload => {
  const { parentNodeUid, childNodeUid, connectionType, stepType } = connectionInfo;
  
  return {
    name: ruleName || `Rule for ${stepType} connection`,
    description: `Auto-generated rule for ${connectionType} connection`,
    sourceStep: parentNodeUid,
    targetStep: childNodeUid,
    connectionType: connectionType,
    conditions: conditions,
    actions: actions
  };
};

/**
 * Creates default conditions based on connection type
 */
export const createDefaultConditions = (
  connectionType: 'onCompleteStep' | 'onFailedStep' | 'onParallelFlow',
  parentStepName: string,
  parentStepId: string
): Array<any> => {
  switch (connectionType) {
    case 'onCompleteStep':
      return [{
        stepName: parentStepName,
        stepId: parentStepId,
        fieldName: 'status',
        operator: 'equals',
        value: 'completed'
      }];
    
    case 'onFailedStep':
      return [{
        stepName: parentStepName,
        stepId: parentStepId,
        fieldName: 'status',
        operator: 'equals',
        value: 'failed'
      }];
    
    case 'onParallelFlow':
      return [{
        stepName: parentStepName,
        stepId: parentStepId,
        fieldName: 'status',
        operator: 'in',
        value: ['completed', 'in_progress']
      }];
    
    default:
      return [];
  }
};

/**
 * Creates default actions based on connection type
 */
export const createDefaultActions = (
  connectionType: 'onCompleteStep' | 'onFailedStep' | 'onParallelFlow',
  targetStepName: string,
  targetStepId: string
): Array<any> => {
  return [{
    type: 'TRIGGER_STEP',
    stepName: targetStepName,
    stepId: targetStepId
  }];
};

/**
 * Main function to create a complete rule for a node connection
 */
export const createNodeConnectionRule = (
  connectionInfo: ConnectionInfo,
  parentStepName: string,
  ruleName?: string
): RuleEnginePayload => {
  const { parentNodeUid, childNodeUid, connectionType, stepType } = connectionInfo;
  
  const conditions = createDefaultConditions(connectionType, parentStepName, parentNodeUid);
  const actions = createDefaultActions(connectionType, stepType, childNodeUid);
  
  return createRuleEnginePayload(
    connectionInfo,
    ruleName || `Auto-Rule: ${parentStepName} -> ${stepType}`,
    conditions,
    actions
  );
};
